<script lang="ts" setup>
  import { BaseInput, VLoader } from '@qic/ui-components'
  import { ref, onMounted, watch } from 'vue'
  import PencilIcon from '../icons/PencilIcon.vue'
  import ArrowIcon from '@/shared/components/icons/ArrowIcon.vue'
  import CrossIcon from '@/shared/components/icons/CrossIcon.vue'
  import { INPUT_MAX_LENGTH } from './BasePromocodeInput.constants'

  interface Props {
    modelValue: string
    validation: (promocode: string) => Promise<boolean>
    isShown?: boolean
    dataTest?: string
  }
  interface Emits {
    (e: 'update:modelValue', promocode: string): void
  }

  const props = withDefaults(defineProps<Props>(), {
    modelValue: '',
    validation: () => Promise.resolve(true),
    isShown: false,
    dataTest: 'base-promocode-input',
  })
  const emit = defineEmits<Emits>()

  const isInputFocused = ref<boolean>(false)
  const isShown = ref<boolean>(props.isShown)
  const isValidationProcess = ref<boolean>(false)
  const isApplied = ref<boolean>(false)
  const isInvalid = ref<boolean>(false)
  const promocode = ref<string>(props.modelValue)

  const onValidation = async () => {
    if (!promocode.value) {
      emit('update:modelValue', '')
      return
    }
    isValidationProcess.value = true
    const isValid = await props.validation(promocode.value)
    isApplied.value = isValid
    isInvalid.value = isValid ? false : !!promocode.value
    if (isValid) {
      emit('update:modelValue', promocode.value)
    } else {
      emit('update:modelValue', '')
    }
    isValidationProcess.value = false
  }

  const change = () => {
    isApplied.value = false
    isInvalid.value = false
  }

  const clear = () => {
    change()
    promocode.value = ''
  }

  const onBlur = () => {
    isInputFocused.value = false
    onValidation()
  }

  onMounted(() => {
    onValidation()
  })

  watch(
    () => props.isShown,
    (newValue) => {
      isShown.value = newValue
    }
  )
</script>

<template>
  <div class="promocode-input" :data-test="dataTest" @keydown.enter="onValidation">
    <div class="promocode-input__header">
      <div class="promocode-input__title" @click="isShown = !isShown">
        <i :class="{ 'promocode-input__icon_show': isShown }" class="promocode-input__icon">
          <ArrowIcon />
        </i>
        <span>{{ $t('qic_have_promocode') }}</span>
      </div>
    </div>
    <transition name="fade">
      <div v-if="isShown" class="promocode-input__input">
        <template v-if="!isApplied && !isInvalid">
          <div class="promocode-input__wrapper-input">
            <BaseInput
              v-model="promocode"
              :max-length="INPUT_MAX_LENGTH"
              num-rtl
              :label="$t('qic_promocode')"
              @focus="isInputFocused = true"
              @blur="onBlur"
              :data-test="`${dataTest}-input`"
            />
            <button
              v-if="isInputFocused || promocode"
              class="promocode-input__btn"
              :disabled="isValidationProcess"
              @mousedown="onValidation"
              :data-test="`${dataTest}-apply`"
            >
              <VLoader v-if="isValidationProcess" color="#fff" />
              <template v-else>
                {{ $t('qic_promocode_apply') }}
              </template>
            </button>
          </div>
        </template>
        <template v-if="isApplied">
          <div class="promocode-input__status promocode-input__status_applied">
            <div class="promocode-input__status-input promocode-input__status-input_applied">
              {{ promocode }}
              <span @click="change">
                <PencilIcon />
              </span>
            </div>
            <p>{{ $t('qic_promocode_applied') }}</p>
          </div>
        </template>
        <template v-if="isInvalid">
          <div class="promocode-input__status promocode-input__status_invalid">
            <div class="promocode-input__status-input promocode-input__status-input_invalid">
              {{ promocode }}
              <span @click="clear" :data-test="`${dataTest}-clear`">
                <CrossIcon />
              </span>
            </div>
            <p>{{ $t('qic_promocode_invalid') }}</p>
          </div>
        </template>
      </div>
    </transition>
  </div>
</template>

<style lang="scss" scoped>
  @import './BasePromocodeInput';
</style>
